import React from 'react';
import { Button, Flex, Text, useToast } from '@chatsdet/ui';
import { Check, Copy } from '@phosphor-icons/react';
import { copyTextToClipboard } from '../../components/settings/clipboard-copy';

const COPIED_RESET_MS = 1500;

/**
 * An email address rendered for a table cell: truncated to the column width
 * with the full address on `title`, selectable by hand, and copyable in one
 * click. The accessible name carries the address and the copied state, since
 * the tooltip is only rendered while hovered.
 */
export function CopyableEmail({ email }: { email: string }) {
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    if (!(await copyTextToClipboard(email))) {
      toast.error('Failed to copy email');
      return;
    }
    setCopied(true);
  };

  return (
    <Flex align="center" gap="xs" className="min-w-0">
      <Text weight="medium" className="select-all truncate" title={email}>
        {email}
      </Text>
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip={copied ? 'Copied' : 'Copy signup email'}
        aria-label={copied ? `Copied ${email}` : `Copy signup email ${email}`}
        onPress={() => void copy()}
      >
        {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
      </Button>
    </Flex>
  );
}
